// src/pages/admin/Reports.tsx - MOCK DATA VERSION
import React, { useState, useEffect } from 'react';
import { 
  Filter, 
  Download, 
  Search, 
  Calendar,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye,
  MessageSquare,
  User
} from 'lucide-react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

const AdminReports: React.FC = () => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      console.log('📡 Fetching reports (mock data)...');
      const response = await apiService.getReports({});
      setReports(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    console.log('Exporting reports...');
    toast.success('Export started (mock)');
  };

  const handleUpdateStatus = async (reportId: string, newStatus: string) => {
    try {
      await apiService.updateReportStatus(reportId, newStatus);
      toast.success('Status updated successfully');
      fetchReports();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const categories = [
    { id: 'water', name: 'Water Issues', color: 'bg-blue-500' },
    { id: 'roads', name: 'Roads & Drainage', color: 'bg-orange-500' },
    { id: 'health', name: 'Health Services', color: 'bg-green-500' },
    { id: 'security', name: 'Security', color: 'bg-red-500' },
    { id: 'employment', name: 'Youth Employment', color: 'bg-purple-500' },
    { id: 'education', name: 'Education', color: 'bg-yellow-500' },
  ];

  const statuses = [
    { id: 'pending', name: 'Pending', icon: <Clock size={16} />, color: 'bg-yellow-100 text-yellow-800' },
    { id: 'verified', name: 'Verified', icon: <CheckCircle size={16} />, color: 'bg-blue-100 text-blue-800' },
    { id: 'in_progress', name: 'In Progress', icon: <AlertTriangle size={16} />, color: 'bg-orange-100 text-orange-800' },
    { id: 'resolved', name: 'Resolved', icon: <CheckCircle size={16} />, color: 'bg-green-100 text-green-800' },
    { id: 'escalated', name: 'Escalated', icon: <MessageSquare size={16} />, color: 'bg-red-100 text-red-800' },
  ];

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'resolved': return <CheckCircle className="text-green-500" size={16} />;
      case 'in_progress': return <Clock className="text-yellow-500" size={16} />;
      case 'verified': return <CheckCircle className="text-blue-500" size={16} />;
      case 'escalated': return <AlertTriangle className="text-red-500" size={16} />;
      default: return <Clock className="text-gray-500" size={16} />;
    }
  };

  const getUrgencyColor = (urgency: number) => {
    if (urgency >= 9) return 'bg-red-500';
    if (urgency >= 7) return 'bg-orange-500';
    if (urgency >= 5) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'verified': return 'bg-blue-100 text-blue-800';
      case 'escalated': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Demo Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Demo Mode</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Reports are showing demonstration data. Connect to backend API when endpoints are available.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Citizen Reports</h1>
          <p className="text-gray-600">Monitor and manage all citizen-reported issues</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            <Download size={16} className="mr-2" />
            Export Reports
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="p-2 bg-blue-50 rounded-lg mr-3">
              <AlertTriangle className="text-blue-500" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Reports</p>
              <p className="text-xl font-bold text-gray-900">{reports.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-yellow-500">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-50 rounded-lg mr-3">
              <Clock className="text-yellow-500" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-xl font-bold text-gray-900">
                {reports.filter(r => r.status === 'PENDING').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="p-2 bg-green-50 rounded-lg mr-3">
              <CheckCircle className="text-green-500" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Resolved</p>
              <p className="text-xl font-bold text-gray-900">
                {reports.filter(r => r.status === 'RESOLVED').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-purple-500">
          <div className="flex items-center">
            <div className="p-2 bg-purple-50 rounded-lg mr-3">
              <MapPin className="text-purple-500" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Wards Covered</p>
              <p className="text-xl font-bold text-gray-900">
                {new Set(reports.map(r => r.wardName)).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">All Reports</h3>
          <p className="text-sm text-gray-500">Showing {reports.length} reports (demo data)</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ward</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Urgency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reports.map((report) => (
                <tr key={report.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">{report.reportId}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{report.description}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {report.phoneNumber} • {new Date(report.reportedAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <MapPin size={14} className="text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{report.wardName || 'Unknown'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {report.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getStatusIcon(report.status)}
                      <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${getStatusColor(report.status)}`}>
                        {report.status.replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`w-16 h-2 rounded-full ${getUrgencyColor(report.urgencyScore)} mr-3`}></div>
                      <span className="text-sm font-medium">{report.urgencyScore}/10</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleUpdateStatus(report.reportId, 'VERIFIED')}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Verify
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(report.reportId, 'RESOLVED')}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                      >
                        Resolve
                      </button>
                      <button
                        onClick={() => toast('View details feature coming soon')}
                        className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminReports;